import { kybService, useToast, CloseCircleOutline } from '@aslanpay/portal-common-lib';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import * as yup from 'yup';

import { KybDeclineModalProps } from './types';

const kybDeclineSchema = yup.object().shape({
  declineReason: yup
    .string()
    .required('Decline reason is mandatory')
    .test('not-empty', 'Decline reason is mandatory', (value) => value?.trim().length > 0),
});

interface KybDeclineFormFields {
  declineReason: string;
}

function KybDeclineModal({ isOpen, onClose, kybId }: KybDeclineModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm<KybDeclineFormFields>({
    mode: 'onChange',
    resolver: yupResolver(kybDeclineSchema),
    defaultValues: {
      declineReason: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDecline = async (data: KybDeclineFormFields) => {
    try {
      await kybService.declineKyb(kybId, data.declineReason);
      await queryClient.invalidateQueries(['fetchKybsPage']);
      toast({
        position: 'top-right',
        title: 'Business declined',
        status: 'success',
        description: 'This business has been declined.',
      });
      reset();
      onClose();
    } catch (error) {
      toast({
        position: 'top-right',
        title: 'Failed to decline business',
        status: 'error',
        description: 'An error occurred while declining this business. Please try again.',
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      onClose={handleClose}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Decline business?</Text>
          </HStack>
        </ModalHeader>
        <ModalBody>
          <Text mb="16px">Please confirm if you want to decline this business.</Text>
          <form id="kyb-decline-form" onSubmit={handleSubmit(handleDecline)} noValidate>
            <FormControl isRequired isInvalid={!!errors?.declineReason?.message}>
              <FormLabel>Decline reason</FormLabel>
              <Textarea
                placeholder="Enter the reason for declining this business"
                rows={4}
                {...register('declineReason')}
              />
              <FormErrorMessage>{errors?.declineReason?.message}</FormErrorMessage>
            </FormControl>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={handleClose} size="sm" isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="kyb-decline-form"
            size="sm"
            color="white"
            bg="system.error"
            leftIcon={<CloseCircleOutline boxSize="20px" />}
            isLoading={isSubmitting}
            isDisabled={!isValid}
          >
            Yes, decline
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default KybDeclineModal;
